import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, Loader2, Send, Settings, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../services/api';

// Dashboard card: share of published products with ≥ 1 review (drives Google aggregateRating / review rich results) + reminder funnel
const ReviewCoverageCard = ({ onOpenSettings }) => {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  useEffect(() => { adminAPI.reviewCoverage().then(setData).catch(() => setData({ error: true })); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await adminAPI.runReviewReminders();
      toast[r.sent ? 'success' : 'info'](r.message);
      setData(d => ({ ...d, reminders: r.reminders }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Envoi impossible'); }
    finally { setRunning(false); }
  };

  if (!data) return <div className="bg-white rounded-xl border border-gray-200 p-5 mb-8 text-sm text-gray-400 inline-flex items-center gap-2" data-testid="review-coverage-card"><Loader2 size={14} className="animate-spin" /> Avis clients…</div>;
  if (data.error) return null;
  const r = data.reminders;
  const pctColor = data.pct >= 50 ? 'text-green-700' : data.pct >= 20 ? 'text-amber-700' : 'text-red-600';
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-8" data-testid="review-coverage-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-base md:text-lg font-semibold text-gray-900 inline-flex items-center gap-2"><Star size={18} className="text-yellow-500 fill-yellow-400" /> Avis clients & étoiles Google</h2>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onOpenSettings} data-testid="review-coverage-settings" className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 border border-gray-300 rounded-md px-2 py-1 hover:bg-gray-50"><Settings size={12} /> Délais : J+{r.days}{r.days_2 ? ` puis J+${r.days_2}` : ' (2e rappel désactivé)'}</button>
          <button type="button" onClick={runNow} disabled={running} data-testid="review-coverage-run" className="inline-flex items-center gap-1 text-xs font-medium text-white bg-[#1e3a5f] hover:bg-[#2d4a6f] rounded-md px-2.5 py-1 disabled:opacity-60">{running ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Lancer les rappels maintenant</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
        <div className="bg-gray-50 rounded-lg p-3" data-testid="review-coverage-pct">
          <p className="text-xs text-gray-500">Produits avec ≥ 1 avis</p>
          <p className={`text-xl font-bold ${pctColor}`}>{data.pct} %</p>
          <p className="text-[11px] text-gray-500">{data.with_reviews} / {data.published} publiés · {data.total_reviews} avis</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3" data-testid="review-coverage-missing">
          <p className="text-xs text-gray-500">Sans avis (Search Console : « aggregateRating / review manquant »)</p>
          <p className="text-xl font-bold text-gray-900">{data.without_reviews}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3" data-testid="review-coverage-reminders">
          <p className="text-xs text-gray-500">Rappels envoyés</p>
          <p className="text-xl font-bold text-gray-900">{r.first_sent}<span className="text-sm font-normal text-gray-500"> + {r.second_sent} derniers rappels</span></p>
          <p className="text-[11px] text-gray-500">{r.awaiting_first} en attente du 1er{r.days_2 ? ` · ${r.awaiting_second} du 2e` : ''}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3" data-testid="review-coverage-conversion">
          <p className="text-xs text-gray-500">Commandes → avis après rappel</p>
          <p className="text-xl font-bold text-gray-900">{r.conversion_pct} %</p>
          <p className="text-[11px] text-gray-500">{r.converted} commande(s) évaluée(s)</p>
        </div>
      </div>
      {data.top_missing.length > 0 && (
        <div data-testid="review-coverage-top">
          <p className="text-xs font-medium text-gray-700 mb-1">Produits les plus vendus sans aucun avis — les étoiles apparaîtront dans Google dès le 1er avis publié :</p>
          <ul className="text-xs text-gray-600 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0.5">
            {data.top_missing.map(p => (
              <li key={p.id} className="flex items-center justify-between gap-2" data-testid={`review-coverage-product-${p.id}`}>
                <span className="truncate">• {p.name} <span className="text-gray-400">{p.brand} · {p.ref}</span></span>
                <span className="shrink-0 inline-flex items-center gap-2">{p.units > 0 ? <span className="text-gray-500">{p.units} vendu{p.units > 1 ? 's' : ''}</span> : <span className="text-gray-400">jamais vendu</span>}<Link to={`/produit/${p.slug || p.id}`} target="_blank" className="text-blue-600 hover:underline inline-flex items-center gap-0.5"><ExternalLink size={11} /></Link></span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-[11px] text-gray-400 mt-3">Les balises aggregateRating et review sont ajoutées automatiquement aux fiches dès qu'un avis vérifié est publié. Google interdit les avis fictifs : seule la collecte d'avis réels (rappels + code promo « Merci ») fait disparaître ces avertissements.</p>
    </div>
  );
};

export default ReviewCoverageCard;
